import { connect } from "react-redux";
import React, { useState, useEffect, useCallback } from "react";
import { withRouter } from "react-router";
import { useTranslation } from "react-i18next";
import LoadingIcon from "mdi-react/LoadingIcon";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import "jspdf-autotable";

import { marketingConstants, paginationConstants } from "../../../constants";
import {
  customToast,
  DownloadExcel,
  convertDateToUTC,
  getFilterExpression,
  formatDateByTimezone,
  getCurrentMonthStartAndEndEpochInMilliseconds,
} from "../../../shared";
import PaginationComponent from "../../../shared/components/PaginationComponent/PaginationComponent";
import { marketingActions } from "../../../redux/actions";

const { GET_REPORTS_DATA } = marketingConstants;

const Reports = (props) => {
  // eslint-disable-next-line
  const [searchText, setSearchText] = useState("");
  const [pagination, setPagination] = useState({
    pageSize: marketingActions.DEFAULT_PAGINATION_PAGE_SIZE,
    currentPage: 1,
    totalRows: 0,
  });
  const [date, setDate] = useState(
    getCurrentMonthStartAndEndEpochInMilliseconds()
  );
  const [filterOptionValue, setFilterOptionValue] = useState("");
  const filterOptions = [
    "Code",
    "PartnerName",
    "PartnerEmail",
    "EventId",
    "EventName",
    "EventHostEmail",
  ];

  const spinner = useCallback(
    (type) => props.spinnerActions.findIndex((f) => f === type),
    [props.spinnerActions]
  );

  const { t } = useTranslation("common");
  const columns = [
    {
      name: t("code"),
      selector: "code",
      sortable: true,
      width: "150px",
      cell: (row) => <div className="word-wrap">{row.code}</div>,
    },
    {
      name: t("partner_name"),
      selector: "partnerName",
      sortable: true,
      width: "200px",
      cell: (row) => <div className="word-wrap">{row.partnerName}</div>,
    },
    {
      name: t("Partner Email"),
      selector: "partnerEmail",
      sortable: true,
      width: "200px",
      cell: (row) => <div className="word-wrap">{row.partnerEmail}</div>,
    },
    {
      name: t("Event Id"),
      selector: "eventId",
      sortable: true,
      width: "130px",
    },
    {
      name: t("event_date"),
      selector: "eventDate",
      sortable: true,
      width: "180px",
      cell: (row) => {
        return formatDateByTimezone(row?.eventDate, row?.timezone);
      },
    },
    {
      name: t("Created At"),
      selector: "createdAt",
      sortable: true,
      width: "180px",
      cell: (row) => {
        return formatDateByTimezone(row?.createdAt);
      },
    },
    {
      name: t("event_name"),
      selector: "eventName",
      sortable: true,
      width: "150px",
      cell: (row) => <div className="word-wrap">{row.eventName}</div>,
    },
    {
      name: t("event_host_email"),
      selector: "eventHostEmail",
      sortable: true,
      width: "250px",
      cell: (row) => <div className="word-wrap">{row.eventHostEmail}</div>,
    },
    {
      name: t("shutterbHours"),
      selector: "shutterbHours",
      sortable: true,
      width: "150px",
    },
    {
      name: t("Hour Cost"),
      selector: "hourCost",
      sortable: true,
      width: "250px",
    },
    {
      name: t("Hours Subtotal"),
      selector: "hourSubtotal",
      sortable: true,
      width: "150px",
    },
    {
      name: t("Recap(z) Story"),
      selector: "reelStory",
      sortable: true,
      width: "150px",
    },
    {
      name: t("Recap Story Cost"),
      selector: "reelStoryCost",
      sortable: true,
      width: "150px",
    },
    {
      name: t("Recap Story Subtotal"),
      selector: "reelStorySubtotal",
      sortable: true,
      width: "180px",
    },
    {
      name: t("Recap(z) Highlight"),
      selector: "reelHighlight",
      sortable: true,
      width: "180px",
    },
    {
      name: t("Recap Highlight Cost"),
      selector: "reelHighlightCost",
      sortable: true,
      width: "180px",
    },
    {
      name: t("Recap Highlight Subtotal"),
      selector: "reelHighlightSubtotal",
      sortable: true,
      width: "200px",
    },
    {
      name: t("Promo Code Applied"),
      selector: "promoCodeApplied",
      sortable: true,
      width: "170px",
    },
    {
      name: t("Affiliate Promo Code Applied"),
      selector: "affiliatePromoCodeApplied",
      sortable: true,
      width: "220px",
    },
    {
      name: t("Promo Code"),
      selector: "promoCode",
      sortable: true,
      width: "150px",
    },
    {
      name: t("Applied Discount"),
      selector: "appliedDiscount",
      sortable: true,
      width: "150px",
    },
    {
      name: t("Total Basket"),
      selector: "totalBasket",
      sortable: true,
      width: "150px",
      cell: (row) => {
        return "$ " + row.totalBasket;
      },
    },
  ];

  const customStyles = {
    headCells: {
      style: {
        fontWeight: "bold",
      },
    },
  };

  useEffect(() => {
    props.getReports(
      pagination.pageSize,
      pagination.pageNumber,
      getFilterExpression(
        paginationConstants.PAGINATION_FILTER_TYPE.LIST_DATE_RANGE,
        searchText,
        filterOptionValue,
        date
      )
    );
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setPagination({
      pageSize: props.reportsData?.pageSize,
      pageNumber: props.reportsData?.currentPage,
      totalRows: props.reportsData?.total,
    });
  }, [props.reportsData]);

  const ExportToExcel = async (excelData) => {
    if (excelData.length > 0) {
      const exportData = [];
      for (let index = 0; index < excelData.length; index++) {
        const element = excelData[index];
        
        // Create a new object for each iteration
        const newData = {
          Code: element.code,
          PartnerName: element.partnerName,
          PartnerEmail: element.partnerEmail,
          EventId: element.eventId,
          EventDate: formatDateByTimezone(element.eventDate, element.timezone),
          CreatedAt: formatDateByTimezone(element.createdAt),
          EventName: element.eventName,
          EventHostEmail: element.eventHostEmail,
          shutterbHours: element.shutterbHours,
          HourCost: element.hourCost,
          HoursSubtotal: element.hourSubtotal,
          ReelStory: element.reelStory,
          ReelStoryCost: element.reelStoryCost,
          ReelStorySubtotal: element.reelStorySubtotal,
          ReelHighlight: element.reelHighlight,
          ReelHighlightCost: element.reelHighlightCost,
          ReelHighlightSubtotal: element.reelHighlightSubtotal,
          PromoCodeApplied: element.promoCodeApplied,
          AffiliatePromoCodeApplied: element.affiliatePromoCodeApplied,
          PromoCode: element.promoCode,
          AppliedDiscount: element.appliedDiscount,
          BasketTotal: "$ " + element.totalBasket,
        };

        // Push the new object into the exportData array
        exportData.push(newData);
      }
      DownloadExcel(exportData, "AffiliateMarketingReport");
    } else {
      customToast("No Data Exists", toast.TYPE.ERROR);
    }
  };

  return (
    <div className="row mr-0">
      <div className="col-md-12">
        <div className="col-md-12 mx-0">
          <div className="tab-content">
            {spinner(GET_REPORTS_DATA) ? (
              <div className="tab-pane fade show active">
                <div className="pagination-controls">
                  <button
                    type="button"
                    className="btn btn-info"
                    onClick={() => ExportToExcel(props?.reportsData?.items)}
                  >
                    Download Excel
                  </button>
                  <PaginationComponent
                    pagination={pagination}
                    setPagination={setPagination}
                    searchText={searchText}
                    setSearchText={setSearchText}
                    getTableData={props.getReports}
                    filterOptionValue={filterOptionValue}
                    setFilterOptionValue={setFilterOptionValue}
                    filterOptions={filterOptions}
                    listDateRange={true}
                    isDateIncluded
                    date={date}
                    setDate={setDate}
                  />
                </div>
                <DataTable
                  columns={columns}
                  data={props.reportsData.items}
                  highlightOnHover
                  responsive
                  customStyles={customStyles}
                  className="sticky-header-class"
                />
              </div>
            ) : (
              <div className="text-center mt-4">
                <LoadingIcon className="loading-spinner" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Mapping the component's props to the reducer's state
const mapStateToProps = (state) => ({
  reportsData: state.marketing.reportsData,
  spinnerActions: state.system.spinnerActions,
});

// Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  getReports: (pageSize, pageIndex, filterExpression) =>
    dispatch(
      marketingActions.getReports(pageSize, pageIndex, filterExpression)
    ),
});

// Mapping action and store the function via props
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(Reports));
